import EventEmitter from './event-emitter.js';

/**
 * SettleEngine - Adaptive friction for iOS-like snap-to-settling
 *
 * Uses adaptive friction to curve smoothly into snap position.
 * Primarily decelerating with tiny attraction near target for clean landing.
 *
 * Algorithm: Each frame, calculate exact friction needed to land on target.
 * Add small attraction force when close to ensure we reach the snap point.
 */
export default class SettleEngine {
	#position = 0;
	#velocity = 0;
	#targetPosition = 0;
	#effectiveFriction = 0.92;
	#attraction = 0.002; // Tiny attraction to help land on target
	#prevTime = null;
	#isRunning = false;
	#eventEmitter;

	constructor() {
		this.#eventEmitter = new EventEmitter();
	}

	/**
	 * Start settling animation toward target position
	 * @param {number} position - Current position
	 * @param {number} velocity - Current velocity (from momentum engine)
	 * @param {number} targetPosition - Target snap position
	 */
	start(position, velocity, targetPosition) {
		const _ = this;
		_.#position = position;
		_.#targetPosition = targetPosition;
		_.#prevTime = null;
		_.#isRunning = true;

		// Ensure minimum velocity toward target - without this, near-zero
		// velocity would leave us stuck (pure deceleration can't accelerate)
		const direction = Math.sign(targetPosition - position);
		const minVelocity = 3;
		if (Math.abs(velocity) < minVelocity || Math.sign(velocity) !== direction) {
			velocity = direction * minVelocity;
		}
		_.#velocity = velocity;

		// Emit initial position
		_.#eventEmitter.emit('engine:position-changed', {
			position: _.#position,
			positionDelta: 0,
			progress: 0,
			velocity: _.#velocity,
		});
	}

	/**
	 * Advance physics by one frame
	 * @param {number} time - Timestamp from frame engine
	 */
	tick(time) {
		const _ = this;
		if (!_.#isRunning) return;

		// Calculate time delta, default to ~60fps if first frame
		let timeDelta = _.#prevTime == null ? 16.67 : time - _.#prevTime;
		_.#prevTime = time;

		// Clamp time delta to prevent huge jumps
		timeDelta = Math.min(timeDelta, 100);

		// Normalize to 60fps
		const timeFactor = timeDelta / 16.67;

		const remainingDistance = _.#targetPosition - _.#position;
		const absVelocity = Math.abs(_.#velocity);
		const absRemaining = Math.abs(remainingDistance);

		// Check if we're done
		if (absRemaining < 0.2) {
			_.#position = _.#targetPosition;
			_.#velocity = 0;
			_.#isRunning = false;

			_.#eventEmitter.emit('engine:position-changed', {
				position: _.#position,
				positionDelta: 0,
				progress: 1,
				velocity: 0,
			});
			_.#eventEmitter.emit('engine:finished');
			return;
		}

		// Calculate exact friction needed to land on target
		// This value naturally stays constant because distance/velocity ratio is preserved
		_.#effectiveFriction = absRemaining / (absVelocity + absRemaining);

		// Apply friction (always decelerating)
		_.#velocity *= Math.pow(_.#effectiveFriction, timeFactor);

		// Add attraction force when close to target
		if (absRemaining < 340) {
			const attractionForce = remainingDistance * _.#attraction * timeFactor;
			_.#velocity += attractionForce;
		}

		// Ensure velocity is moving toward target (prevent overshoot oscillation)
		const velocitySign = Math.sign(_.#velocity);
		const targetSign = Math.sign(remainingDistance);
		if (velocitySign !== 0 && targetSign !== 0 && velocitySign !== targetSign) {
			// Velocity pointing away from target - reduce it faster
			_.#velocity *= 0.5;
		}

		// Update position
		const posDelta = _.#velocity * timeFactor;
		_.#position += posDelta;

		// Check for overshoot and correct
		const newRemaining = _.#targetPosition - _.#position;
		if (Math.sign(newRemaining) !== Math.sign(remainingDistance) && absRemaining > 1) {
			// We crossed the target - snap to it
			_.#position = _.#targetPosition;
			_.#velocity = 0;
			_.#isRunning = false;

			_.#eventEmitter.emit('engine:position-changed', {
				position: _.#position,
				positionDelta: 0,
				progress: 1,
				velocity: 0,
			});
			_.#eventEmitter.emit('engine:finished');
			return;
		}

		_.#eventEmitter.emit('engine:position-changed', {
			position: _.#position,
			positionDelta: posDelta,
			progress: 0,
			velocity: _.#velocity,
		});
	}

	/**
	 * Update position directly (used when track shifts for looping)
	 * @param {number} delta - Amount to add to position
	 */
	shiftPosition(delta) {
		this.#position += delta;
		this.#targetPosition += delta;
	}

	/**
	 * Stop immediately
	 */
	stop() {
		this.#velocity = 0;
		this.#isRunning = false;
	}

	get position() {
		return this.#position;
	}

	get velocity() {
		return this.#velocity;
	}

	get isRunning() {
		return this.#isRunning;
	}

	/**
	 * Add event listener
	 * @param {string} eventName - Event name
	 * @param {function} eventFunction - Handler function
	 */
	on(eventName, eventFunction) {
		this.#eventEmitter.on(eventName, eventFunction);
	}

	/**
	 * Remove event listener
	 * @param {string} eventName - Event name
	 * @param {function} eventFunction - Handler function
	 */
	off(eventName, eventFunction) {
		this.#eventEmitter.off(eventName, eventFunction);
	}

	destroy() {
		this.stop();
		this.#eventEmitter.destroy();
		this.#eventEmitter = null;
	}
}
